<script setup lang="ts">
import { Link } from "@inertiajs/vue3";
import { AlertTriangleIcon, InfoIcon } from "lucide-vue-next";
import { computed } from "vue";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export type GlobalNoticeVariant = "warning" | "info";

export interface GlobalNotice {
  id: string;
  priority: number;
  variant: GlobalNoticeVariant;
  title: string;
  description: string;
  dismissible?: boolean;
  cta?: {
    label: string;
    href: string;
  };
}

const props = withDefaults(defineProps<{
  notices?: GlobalNotice[];
}>(), {
  notices: () => [],
});

const sortedNotices = computed(() => {
  return [...props.notices].sort((left, right) => right.priority - left.priority);
});

const NOTICE_VARIANT_STYLES: Record<GlobalNoticeVariant, { alert: string; icon: string; cta: string }> = {
  warning: {
    alert: "border-amber-400 bg-amber-100 text-amber-900 dark:border-amber-500/60 dark:bg-amber-950/60 dark:text-amber-200",
    icon: "text-amber-700 dark:text-amber-300",
    cta: "font-medium underline underline-offset-4 text-amber-900 hover:text-amber-700 dark:text-amber-200 dark:hover:text-amber-100",
  },
  info: {
    alert: "border-sky-300/70 bg-sky-100/50 text-sky-950 dark:border-sky-900/70 dark:bg-sky-950/30 dark:text-sky-100",
    icon: "text-sky-700 dark:text-sky-300",
    cta: "font-medium underline underline-offset-4 text-sky-900 hover:text-sky-700 dark:text-sky-200 dark:hover:text-sky-100",
  },
};

const stylesFor = (variant: GlobalNoticeVariant) => NOTICE_VARIANT_STYLES[variant];
</script>

<template>
  <div v-if="sortedNotices.length > 0" class="mx-auto mt-2 flex max-w-7xl flex-col gap-2">
    <Alert
      v-for="notice in sortedNotices"
      :key="notice.id"
:class="stylesFor(notice.variant).alert"
    >
      <AlertTriangleIcon v-if="notice.variant === 'warning'" :class="stylesFor(notice.variant).icon" />
      <InfoIcon v-else :class="stylesFor(notice.variant).icon" />

      <AlertTitle class="line-clamp-none">{{ notice.title }}</AlertTitle>
      <AlertDescription class="gap-2 text-sm [&_p]:leading-relaxed">
        <p>
          {{ notice.description }}
          <Link
            v-if="notice.cta"
            :href="notice.cta.href"
:class="stylesFor(notice.variant).cta"
          >
            {{ notice.cta.label }}
          </Link>
        </p>
      </AlertDescription>
    </Alert>
  </div>
</template>
